import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

function Toggle({ on, onClick, testid, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid} role="switch" aria-checked={on} data-state={on ? "on" : "off"} className={cn("relative h-6 w-11 rounded-full transition-colors disabled:opacity-40", on ? "aura-gradient" : "bg-am-surface3")}>
      <span className={cn("absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all", on ? "left-[22px]" : "left-0.5")} />
    </button>
  );
}

const ROWS = [
  { k: "follows", l: "New followers", d: "When someone follows you" },
  { k: "comments", l: "Comments on my posts", d: "When someone comments on your post" },
  { k: "replies", l: "Replies to my comments", d: "When someone replies to your comment" },
];

// Settings → Notifications → Email. Saved server-side as users.email_prefs.
export function EmailPrefs() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.emailPrefs().then(setData).catch(() => setData({ prefs: {} })); }, []);
  const set = async (k, v) => {
    setData((d) => ({ ...d, prefs: { ...d.prefs, [k]: v } }));
    try { setData(await api.setEmailPrefs({ [k]: v })); } catch (e) { toast(e.message); }
  };
  if (!data) return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-am-text3" /></div>;
  const p = data.prefs;
  return (
    <div data-testid="email-prefs" className="pt-2">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-white"><Mail className="h-4 w-4 aura-text" /> Email</div>
      <p className="mb-2 text-xs text-am-text2">We group activity into a few emails a day and never email you while you're active in the app.</p>
      {data.email_status && <p className="mb-2 rounded-lg bg-aura-magenta/10 px-3 py-2 text-xs text-aura-magenta" data-testid="email-status-warning">Emails to your address {data.email_status === "bounced" ? "bounced" : "were marked as spam"}, so they're paused. Update your email in Account &amp; Security to resume.</p>}
      <div className="flex items-center justify-between py-3">
        <div><div className="text-sm font-semibold text-white">Email notifications</div><div className="text-xs text-am-text2">Master switch for all notification emails</div></div>
        <Toggle on={!!p.all} onClick={() => set("all", !p.all)} testid="email-toggle-all" />
      </div>
      {ROWS.map((row) => (
        <div key={row.k} className={cn("flex items-center justify-between py-3", !p.all && "opacity-50")}>
          <div><div className="text-sm font-semibold text-white">{row.l}</div><div className="text-xs text-am-text2">{row.d}</div></div>
          <Toggle on={!!p[row.k]} onClick={() => set(row.k, !p[row.k])} testid={`email-toggle-${row.k}`} disabled={!p.all} />
        </div>
      ))}
      <p className="mt-2 text-[11px] text-am-text3">Sent from notifications@auramax.dev · time zone {data.timezone}. <button onClick={() => navigate("/settings")} className="underline">Manage</button></p>
    </div>
  );
}
